/**
 * HonorarRechner — BVSK-Honorar-Rechner für Kfz-Gutachten.
 *
 * Berechnet das Sachverständigenhonorar nach der BVSK-Honorartabelle (HB-V)
 * 2024 — der gängige Branchenstandard für Schadengutachten in Deutschland.
 * Stützpunkte sind interpoliert; Nebenkosten als Pauschalen.
 *
 * Quellen: BVSK Honorartabelle HB-V 2024 (Korridor V) ·
 *   https://www.bvsk.de/honorartabelle
 */
import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, ChevronRight, Info } from "lucide-react";

// BVSK Stützpunkte (Schadenhöhe brutto → Grundhonorar netto, ca-Werte HB-V Korridor V)
const TABLE = [
  { schaden: 750,    honorar: 161 },
  { schaden: 1000,   honorar: 199 },
  { schaden: 1500,   honorar: 240 },
  { schaden: 2000,   honorar: 295 },
  { schaden: 3000,   honorar: 376 },
  { schaden: 4000,   honorar: 451 },
  { schaden: 5000,   honorar: 522 },
  { schaden: 7500,   honorar: 700 },
  { schaden: 10000,  honorar: 853 },
  { schaden: 12500,  honorar: 990 },
  { schaden: 15000,  honorar: 1108 },
  { schaden: 20000,  honorar: 1247 },
  { schaden: 25000,  honorar: 1369 },
  { schaden: 30000,  honorar: 1476 },
  { schaden: 40000,  honorar: 1700 },
  { schaden: 50000,  honorar: 1910 },
];

function calcHonorar(schaden) {
  if (!schaden || schaden <= 0) return 0;
  if (schaden < TABLE[0].schaden) {
    return Math.round((schaden / TABLE[0].schaden) * TABLE[0].honorar);
  }
  for (let i = 0; i < TABLE.length - 1; i++) {
    const a = TABLE[i], b = TABLE[i + 1];
    if (schaden >= a.schaden && schaden <= b.schaden) {
      const ratio = (schaden - a.schaden) / (b.schaden - a.schaden);
      return Math.round(a.honorar + ratio * (b.honorar - a.honorar));
    }
  }
  // Extrapolation für sehr große Schäden — degressiv mit ~0,016 €/€ über 50k
  const last = TABLE[TABLE.length - 1];
  return Math.round(last.honorar + (schaden - last.schaden) * 0.016);
}

const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmt2 = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);

export default function HonorarRechner({ compact = false }) {
  const [schadenStr, setSchadenStr] = useState("3000");
  const [photos, setPhotos] = useState(10);
  const [pages, setPages] = useState(12);
  const [km, setKm] = useState(20);

  const schaden = parseFloat(schadenStr.replace(/\./g, "").replace(",", ".")) || 0;

  const result = useMemo(() => {
    const grund = calcHonorar(schaden);
    const fotos = Math.max(0, photos) * 2.55;
    const schreib = Math.max(0, pages) * 1.80;
    const fahrt = Math.max(0, km) * 0.70;
    const auslagen = 15.00;
    const netto = grund + fotos + schreib + fahrt + auslagen;
    const mwst = netto * 0.19;
    const brutto = netto + mwst;
    return { grund, fotos, schreib, fahrt, auslagen, netto, mwst, brutto };
  }, [schaden, photos, pages, km]);

  return (
    <div
      className={`rounded-3xl border border-cyan-100 bg-gradient-to-br from-cyan-50/60 via-white to-emerald-50/40 ${compact ? "p-5 sm:p-7" : "p-6 sm:p-10"}`}
      data-testid="honorar-rechner"
    >
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-2xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Calculator className="h-5 w-5 text-white" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">BVSK-Honorartabelle 2024</div>
          <h3 className={`font-display ${compact ? "text-xl" : "text-2xl sm:text-3xl"} font-bold tracking-tight mt-0.5`}>
            Was kostet ein Gutachten?
          </h3>
          <p className="text-sm text-zinc-600 mt-1.5">
            Sofort-Schätzung nach Branchenstandard — Reparaturkosten + Nebenkosten + 19 % MwSt.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-start">
        {/* Inputs */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="rechner-schaden" className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">
              Reparaturkosten (Schadenhöhe)
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="rechner-schaden"
                value={schadenStr}
                onChange={(e) => setSchadenStr(e.target.value.replace(/[^\d.,]/g, ""))}
                placeholder="3000"
                inputMode="numeric"
                className="h-12 text-xl font-bold pr-10"
                data-testid="rechner-schaden-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 font-semibold">€</span>
            </div>
            <input
              type="range"
              min="500"
              max="50000"
              step="100"
              value={Math.min(50000, schaden)}
              onChange={(e) => setSchadenStr(e.target.value)}
              className="w-full mt-2 accent-cyan-600"
              data-testid="rechner-schaden-slider"
            />
            <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
              <span>500 €</span><span>10 k</span><span>25 k</span><span>50 k €</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2">
            <NumberField label="Lichtbilder" value={photos} onChange={setPhotos} suffix="Stk" testid="rechner-photos" />
            <NumberField label="Gutachten-Seiten" value={pages} onChange={setPages} suffix="S" testid="rechner-pages" />
            <NumberField label="Anfahrt (einfach)" value={km} onChange={setKm} suffix="km" testid="rechner-km" />
          </div>

          <div className="flex items-start gap-2 text-[12px] text-zinc-500 pt-2">
            <Info className="h-3.5 w-3.5 text-cyan-600 shrink-0 mt-0.5" />
            <p>
              Bei <strong>unverschuldetem Unfall</strong> trägt die gegnerische Haftpflicht die Gutachterkosten zu 100 %
              (BGH-Rechtsprechung, § 249 BGB).
            </p>
          </div>
        </div>

        {/* Result */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 shadow-sm min-w-[260px]" data-testid="rechner-result">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-1">Geschätztes Gesamthonorar</div>
          <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            {fmt(result.brutto)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">brutto · inkl. 19 % MwSt</div>
          <div className="mt-5 space-y-1.5 text-[13px]">
            <Row label="Grundhonorar" value={fmt2(result.grund)} primary />
            <Row label={`Lichtbilder (${photos}×)`} value={fmt2(result.fotos)} />
            <Row label={`Schreibkosten (${pages} Seiten)`} value={fmt2(result.schreib)} />
            <Row label={`Anfahrt (${km}×2 km)`} value={fmt2(result.fahrt)} />
            <Row label="Allgemeine Auslagen" value={fmt2(result.auslagen)} />
            <hr className="my-2 border-zinc-200" />
            <Row label="Netto" value={fmt2(result.netto)} bold />
            <Row label="MwSt. 19 %" value={fmt2(result.mwst)} />
            <Row label="Brutto" value={fmt2(result.brutto)} highlight />
          </div>
          <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-700">
            BVSK HB-V 2024 · Korridor V <ChevronRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
      <div className="relative mt-1">
        <Input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          className="h-9 pr-8 text-[13px]"
          data-testid={testid}
        />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-zinc-400">{suffix}</span>}
      </div>
    </div>
  );
}

function Row({ label, value, primary, bold, highlight }) {
  return (
    <div className={`flex items-center justify-between ${highlight ? "text-cyan-700 font-bold" : bold ? "text-zinc-900 font-semibold" : "text-zinc-700"}`}>
      <span className={primary ? "font-semibold text-zinc-900" : ""}>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
